import { gql, MutationResult, useMutation } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { MutationReturnType } from 'basics/types/maya.types';
import { SyncVeevaTargets } from 'generated/maya.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const syncTargetsByMdmIdListToVeevaMutationCall = gql`
  mutation syncTargetsByMdmIdListToVeeva($cepId: String!, $mdmIdList: [String!]!, $syncSelectedTargets: Boolean!) {
    syncTargetsByMdmIdListToVeeva(cepId: $cepId, mdmIdList: $mdmIdList, syncSelectedTargets: $syncSelectedTargets) {
      syncVeeva,
      errors
    }
  }
`;

type ResultType = {
  syncTargetsByMdmIdListToVeeva: SyncVeevaTargets;
};

type UseSyncTargetsByMdmIdListToVeevaMutation = [
  (cepId: string, mdmIdList: string[]) => Promise<MutationReturnType<SyncVeevaTargets | undefined>>,
  MutationResult<ResultType>,
];

export const useSyncTargetsByMdmIdListToVeevaMutation = (): UseSyncTargetsByMdmIdListToVeevaMutation => {
  const [syncTargetsByMdmIdListToVeeva, state] = useMutation<ResultType>(syncTargetsByMdmIdListToVeevaMutationCall, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });

  const flags = useFlags();

  const executeMutation = useCallback(async (cepId, mdmIdList) => {
    try {
      const { data } = await syncTargetsByMdmIdListToVeeva({
        variables: {
          cepId,
          mdmIdList,
          syncSelectedTargets: flags.akma654EnableSelectiveTargetSync,
        },
      });

      return { data: data?.syncTargetsByMdmIdListToVeeva, error: null };
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error);
        Logger.logError({
          error,
          customContext,
          transactionName: 'syncTargetsToVeeva.ts - useSyncTargetsToVeevaMutation',
        });

        return { data: null, error: customContext.message as string };
      }

      return { data: null, error: null };
    }
  }, [syncTargetsByMdmIdListToVeeva, flags.akma654EnableSelectiveTargetSync]);

  return [executeMutation, state];
};
